/**
 * PurchasePanel — SEP 5.b.2.
 *
 * Consumes a `selection: GridSelection | null` PROP produced by the
 * shared GridViewer's native rectangle drag (see App composition on
 * route #purchase). The runtime x,y,w,h text input has been REMOVED
 * (spec §H); the panel now:
 *
 *   * refetches the quote automatically when the committed selection
 *     changes (§F: quote invalidation is immediate),
 *   * discards stale results via a race-guarded QuoteLoader (§V.32),
 *   * derives financial readiness from the ACTIVE quote total
 *     (recomputes on every selection change),
 *   * fires no fetches while the selection is null.
 *
 * No transactions (approve/purchase) here — that lives in 5.c/5.d.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePublicClient } from "wagmi";
import { useI18n } from "../i18n";
import { ApiError } from "../lib/api";
import { formatMicrousdc } from "../lib/money";
import { saleApi } from "../lib/sale-api";
import type { GridSelection } from "../lib/grid-selection";
import { selectionsEqual } from "../lib/grid-selection";
import type { OccupancyRect } from "../lib/selection-occupancy";
import { deriveOccupancyGuard } from "../lib/selection-occupancy";
import { QuoteLoader } from "../lib/purchase-quote-loader";
import { mapSaleErrorCategory, labelForSaleErrorCategory } from "../lib/sale-errors";
import type { SaleConfig, SaleQuote, SaleStatus } from "../lib/sale-types";
import { derivePurchaseGate } from "../lib/sale-status-gate";
import { useWalletPreflight } from "../lib/wallet-preflight";
import { useManualChainSwitch } from "../lib/chain-switch";
import {
  ERC20_READ_ABI,
  evaluateFinancialReadGate,
  useUsdcAllowance,
  useUsdcBalanceOf,
} from "../lib/usdc-reads";
import { deriveFinancialReadiness } from "../lib/financial-readiness";
import { SUPPORTED_CHAIN_IDS } from "../lib/chain";
import {
  deriveApproveEligibility,
  shouldApplyApproveConfirmation,
  useUsdcApprove,
} from "../lib/usdc-approve";
import {
  derivePurchaseEligibility,
  preparePurchase,
  useSaleV2Purchase,
} from "../lib/salev2-purchase";
import { usePurchaseIndexing } from "../lib/purchase-indexing";
import { shouldTriggerGridRefresh } from "../lib/purchase-refresh";
import {
  clearRecovery,
  loadValidRecovery,
  persistRecovery,
  resolveRecoveryOutcome,
  type ReceiptProbe,
  type RecoveryPhase,
} from "../lib/purchase-recovery";

interface QuoteState {
  kind: "idle" | "loading" | "success" | "error";
  received_at?: number;
  quote?: SaleQuote;
  errorCode?: string;
  errorMessage?: string;
  errorCategory?: string;
  errorReason?: string;
}

export interface PurchasePanelProps {
  /** Canonical selection produced by the shared GridViewer. */
  selection: GridSelection | null;
  /**
   * Macro #21 §5 — canonical (indexer-sourced) parcel footprints already
   * loaded by the viewer. Consumed ONLY for a FAIL-FAST occupancy guard:
   * if the drawn selection overlaps existing land the purchase is blocked
   * before the wallet. Absence of overlap is NOT availability — quote +
   * preflight remain mandatory and the chain (ErrOverlap) is final.
   */
  occupancy?: OccupancyRect[];
  /** Optional callback to clear the parent's committed selection. */
  onClearSelection?: () => void;
  /**
   * HOTFIX purchase-indexed-grid-refresh — fired ONCE when a purchase reaches
   * `indexed_successfully` (backend-authoritative). The shell uses it to
   * refresh the shared Grid so the newly indexed parcel appears without a
   * manual browser refresh. NOT fired on confirmed_onchain / pending / delayed
   * / anomaly.
   */
  onPurchaseIndexed?: () => void;
}

export default function PurchasePanel({
  selection,
  occupancy,
  onClearSelection,
  onPurchaseIndexed,
}: PurchasePanelProps) {
  const { t } = useI18n();
  const [config, setConfig] = useState<SaleConfig | null>(null);
  const [status, setStatus] = useState<SaleStatus | null>(null);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [quoteState, setQuoteState] = useState<QuoteState>({ kind: "idle" });

  // Race-guarded loader (SEP 5.b.2 §V.32 — stale outcomes discarded).
  const loaderRef = useRef<QuoteLoader | null>(null);
  if (loaderRef.current == null) {
    loaderRef.current = new QuoteLoader(saleApi.quote);
  }
  // Track the LAST selection we launched a load for, to avoid redundant
  // fetches when a parent re-renders with an equal selection.
  const lastFetchedRef = useRef<GridSelection | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          saleApi.config(),
          saleApi.status(),
        ]);
        setConfig(c);
        setStatus(s);
      } catch (e) {
        setBootstrapError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const expectedChainId = useMemo(() => {
    if (config == null) return 0;
    return config.chain_id ?? 0;
  }, [config]);

  const preflight = useWalletPreflight(expectedChainId);
  const gate = derivePurchaseGate(status);
  const chainSwitch = useManualChainSwitch();
  const publicClient = usePublicClient();

  // Macro #21 §5 — FAIL-FAST occupancy guard. Positive canonical overlap
  // evidence (indexer-sourced, already on the page) blocks the purchase
  // BEFORE the wallet. Never grants availability; quote/preflight/chain stay
  // authoritative (ErrOverlap is the final guard against stale/lag races).
  const occupancyGuard = useMemo(
    () => deriveOccupancyGuard(selection, occupancy ?? [], preflight.address ?? null),
    [selection, occupancy, preflight.address],
  );
  const occupancyBlocked = occupancyGuard.blocked;

  // Financial read gate + reads. Only fire when ALL prerequisites hold
  // (config v2 + status ok + wallet on correct chain + supported chain).
  const readGate = evaluateFinancialReadGate(
    { config, status, preflight },
    SUPPORTED_CHAIN_IDS,
  );
  const readCtx = readGate.allowed ? readGate.ctx : null;
  const balanceQ = useUsdcBalanceOf(readCtx);
  const allowanceQ = useUsdcAllowance(readCtx);

  // ---- SEP 5.b.2/5.c: quote fetch orchestration ----
  // `runQuoteLoad` is the ONE entry point that launches a quote fetch.
  // The selection-change effect calls it with dedup; forced re-quotes
  // (post-approve, §L/§M; pre-purchase click, 5.d §C) call it DIRECTLY,
  // bypassing selectionsEqual.
  //
  // Returns the outcome so 5.d can await the fresh quote before firing
  // the SaleV2.purchase write.
  const runQuoteLoad = useCallback(async (sel: GridSelection) => {
    lastFetchedRef.current = { ...sel };
    setQuoteState({ kind: "loading" });
    const loader = loaderRef.current!;
    const outcome = await loader.load(sel);
    if (!outcome.isCurrent) return outcome; // §V.32 race guard
    if (outcome.result.kind === "success") {
      setQuoteState({
        kind: "success",
        received_at: Date.now(),
        quote: outcome.result.quote,
      });
    } else {
      const err = outcome.result.error;
      const code = err instanceof ApiError ? err.code : "UNKNOWN";
      const details =
        err instanceof ApiError
          ? (err.details as Record<string, unknown> | null)
          : null;
      const cat = mapSaleErrorCategory(code, details);
      const reason =
        details && typeof details === "object" && "reason" in details
          ? String((details as { reason?: unknown }).reason || "")
          : undefined;
      setQuoteState({
        kind: "error",
        errorCode: code,
        errorMessage: err instanceof Error ? err.message : String(err),
        errorCategory: cat,
        errorReason: reason,
      });
    }
    return outcome;
  }, []);

  // Forced re-quote: DELIBERATELY bypasses the deep-equal dedup in the
  // selection-change effect. Used ONLY by the post-approve confirmation
  // flow (§L/§M). Increments QuoteLoader.gen so any in-flight stale
  // quote resolved AFTER this call is discarded (§N).
  const forceRequote = useCallback(
    (sel: GridSelection) => {
      void runQuoteLoad(sel);
    },
    [runQuoteLoad],
  );

  // ---- SEP 5.b.2: refetch quote when the committed selection changes ----
  useEffect(() => {
    // Nothing selected → clear quote state and skip fetch.
    if (selection == null) {
      lastFetchedRef.current = null;
      setQuoteState({ kind: "idle" });
      return;
    }
    // Backend not ready → do not fetch.
    if (!gate.canStartPurchase) {
      return;
    }
    // Idempotence: same rectangle → do nothing (§F: no redundant fetch).
    // Forced re-quotes bypass this via `forceRequote()` above.
    if (selectionsEqual(lastFetchedRef.current, selection)) {
      return;
    }
    void runQuoteLoad(selection);
  }, [selection, gate.canStartPurchase, runQuoteLoad]);

  // Financial readiness — only meaningful with a SELLABLE V2 quote and
  // reads available. Recomputed automatically on every quote change.
  const currentQuote =
    quoteState.kind === "success" ? quoteState.quote : null;
  const requiredTotal =
    currentQuote &&
    currentQuote.generation === "v2" &&
    currentQuote.sellable &&
    currentQuote.total_price_microusdc != null
      ? currentQuote.total_price_microusdc
      : null;
  const readiness =
    readGate.allowed &&
    requiredTotal != null &&
    balanceQ.data != null &&
    allowanceQ.data != null
      ? deriveFinancialReadiness({
          balance: balanceQ.data,
          allowance: allowanceQ.data,
          required: requiredTotal,
        })
      : null;

  // ---- SEP 5.c: exact USDC approve + mandatory forced re-quote ----
  const approve = useUsdcApprove();
  const approveEligibility = useMemo(
    () =>
      deriveApproveEligibility({
        readCtx,
        quote: currentQuote ?? null,
        readiness,
        selection,
      }),
    [readCtx, currentQuote, readiness, selection],
  );

  // Track which confirmed tx hashes have already triggered the forced
  // requote so we apply the consequences EXACTLY ONCE per confirmation.
  const appliedApproveTxHashRef = useRef<string | null>(null);

  useEffect(() => {
    if (approve.state.kind !== "confirmed") return;
    const txHash = approve.state.txHash;
    if (appliedApproveTxHashRef.current === txHash) return;

    // §P/§Q/§R/§S — context-drift guard. If the current UI context is
    // no longer the one the approve was authorized for, DO NOT apply.
    const current = readCtx
      ? {
          chainId: readCtx.chainId,
          owner: readCtx.owner,
          token: readCtx.token,
          spender: readCtx.spender,
          selection,
        }
      : null;
    if (current == null) return;
    if (!shouldApplyApproveConfirmation(approve.state, current)) return;

    // Mark applied BEFORE launching so we don't re-enter.
    appliedApproveTxHashRef.current = txHash;
    // §T: real on-chain allowance refetch (never optimistic).
    allowanceQ.refetch();
    // §L/§M: forced /sale/quote for the CURRENT selection, bypassing
    // the deep-equal dedup in the selection-change effect.
    if (selection != null) forceRequote(selection);
  }, [approve.state, readCtx, selection, allowanceQ, forceRequote]);

  const onApproveClick = useCallback(() => {
    if (!approveEligibility.eligible) return;
    if (occupancyBlocked) return; // §5 fail-fast — never approve for occupied land
    if (approve.isPending) return;
    void approve.approve(approveEligibility.snapshot);
  }, [approveEligibility, approve, occupancyBlocked]);

  // ---- SEP 5.d: SaleV2.purchase ----
  const purchase = useSaleV2Purchase();
  const purchaseEligibility = useMemo(
    () =>
      derivePurchaseEligibility({
        readCtx,
        quote: currentQuote ?? null,
        readiness,
        selection,
      }),
    [readCtx, currentQuote, readiness, selection],
  );

  /**
   * §5.d.2 sequence D — hard-authoritative pre-write.
   *
   * Order (ratified by Neo):
   *   1. terminal-state guards (5.d.1)
   *   2. beginRefreshQuote() locks the button
   *   3. runQuoteLoad(selection) — forced final quote, race-guarded
   *   4. parallel fresh reads: /sale/config + /sale/status +
   *      publicClient.readContract(balanceOf) + publicClient.readContract(allowance)
   *   5. preparePurchase() validates the 7-check config/status identity,
   *      revalidates financials on the fetch RETURN VALUES (never on the
   *      React hook cache), computes deadline = now+300, builds the
   *      immutable snapshot, and runs the final context sanity.
   *   6. controller.purchase(snapshot) — the SaleV2.purchase write.
   *
   * Fail-closed on any RPC/HTTP failure, any unusable value, any mismatch.
   * No optimistic assumptions. No polling. No dependency on React render
   * timing after the click.
   */
  const onPurchaseClick = useCallback(async () => {
    if (purchase.isPending) return;
    // §5.d.1 terminal duplicate-write guards.
    if (purchase.state.kind === "confirmed_onchain") return;
    if (purchase.state.kind === "error" && purchase.state.txHash != null) return;
    if (occupancyBlocked) return; // §5 fail-fast — never reach the wallet for occupied land
    if (!purchaseEligibility.eligible) return;
    if (!readGate.allowed) return;
    if (selection == null) return;
    if (publicClient == null) return;

    // §5.d.2 — capture click-time identity BEFORE any await.
    const clickCtx = {
      chainId: readGate.ctx.chainId,
      owner: readGate.ctx.owner,
      saleAddress: readGate.ctx.spender,
      paymentToken: readGate.ctx.token,
    } as const;

    const c = purchase.controller;
    c.beginRefreshQuote();

    // Step 3 — force final race-guarded re-quote.
    const outcome = await runQuoteLoad(selection);
    if (outcome == null || !outcome.isCurrent) {
      c.failRefresh("stale_final_quote");
      return;
    }
    if (outcome.result.kind !== "success") {
      c.failRefresh("final_quote_failed");
      return;
    }
    const fresh = outcome.result.quote;

    // Steps 4 + 5 — fresh reads (parallelized) + preparePurchase.
    // The fetchers each throw on failure; preparePurchase Promise.all's
    // them and fails closed on any rejection or unusable value.
    const result = await preparePurchase(
      { clickCtx, selection, freshQuote: fresh },
      {
        fetchFreshConfig: () => saleApi.config(),
        fetchFreshStatus: () => saleApi.status(),
        fetchFreshBalance: () =>
          publicClient.readContract({
            abi: ERC20_READ_ABI,
            address: clickCtx.paymentToken,
            functionName: "balanceOf",
            args: [clickCtx.owner],
          }) as Promise<bigint>,
        fetchFreshAllowance: () =>
          publicClient.readContract({
            abi: ERC20_READ_ABI,
            address: clickCtx.paymentToken,
            functionName: "allowance",
            args: [clickCtx.owner, clickCtx.saleAddress],
          }) as Promise<bigint>,
      },
    );
    if (result.kind === "abort") {
      c.failRefresh(result.reason);
      return;
    }

    // Step 6 — fire the write.
    await c.purchase(result.snapshot);
  }, [
    purchase, purchaseEligibility, readGate, selection,
    runQuoteLoad, publicClient, occupancyBlocked,
  ]);

  // ---- SEP 5.e: backend indexer confirmation ----
  const indexing = usePurchaseIndexing();

  // §I — start indexing polling ONLY when purchase hits confirmed_onchain
  // with a real txHash + snapshot. The controller is idempotent on
  // repeat calls with the same identity (state.kind !== "idle" → no-op).
  useEffect(() => {
    if (purchase.state.kind !== "confirmed_onchain") return;
    if (indexing.state.kind !== "idle") return;
    indexing.controller.start(purchase.state.txHash, purchase.state.snapshot);
  }, [purchase.state, indexing.state.kind, indexing.controller]);

  // HOTFIX purchase-indexed-grid-refresh — notify the shell to refresh the
  // shared Grid ONCE the backend has authoritatively indexed the purchase, so
  // the new parcel appears without a manual browser refresh. Keyed on the
  // indexing state kind so it fires exactly on the transition into
  // `indexed_successfully` (never on confirmed_onchain / pending / delayed /
  // anomaly), and never loops (kind only re-enters success after a reset).
  useEffect(() => {
    if (shouldTriggerGridRefresh(indexing.state.kind)) {
      onPurchaseIndexed?.();
    }
  }, [indexing.state.kind, onPurchaseIndexed]);

  // §7 — reload/recovery. Persist a MINIMAL, non-secret recovery context the
  // moment a real tx exists (submitted/confirming/confirmed) so a page reload
  // never loses an in-flight purchase. Price/deadline/secrets are NEVER stored.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const st = purchase.state;
    const hasTx =
      (st.kind === "submitted" ||
        st.kind === "confirming" ||
        st.kind === "confirmed_onchain") &&
      st.txHash != null;
    if (!hasTx) return;
    const snap = st.snapshot;
    persistRecovery(window.localStorage, {
      wallet: snap.owner,
      chainId: snap.chainId,
      saleAddr: snap.saleAddress,
      txHash: st.txHash,
      geometry: {
        x: snap.selection.x,
        y: snap.selection.y,
        w: snap.selection.w,
        h: snap.selection.h,
      },
      createdAtMs: Date.now(),
    });
  }, [purchase.state]);

  // Clear recovery on a real terminal outcome: OWNED (canonical index),
  // reverted, or wallet rejection (no tx). An `error` WITH a txHash keeps the
  // context so the UNKNOWN on-chain outcome can still be recovered later.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const indexKind = indexing.state.kind;
    const owned = indexKind === "indexed_successfully";
    const failed =
      purchase.state.kind === "reverted" || purchase.state.kind === "rejected";
    if (owned || failed) clearRecovery(window.localStorage);
  }, [indexing.state.kind, purchase.state.kind]);

  // Resume tracking after a reload — ONCE, fail-closed against the CURRENT
  // runtime identity (wallet + chain + SaleV2). Never sends a new tx, never
  // reopens the wallet, never asserts optimistic ownership: it only restarts
  // the canonical indexer poll for the already-submitted txHash. A reverted or
  // stale context is auto-cleaned by loadValidRecovery.
  const recoveryResumedRef = useRef(false);
  const [recoveryPhase, setRecoveryPhase] = useState<RecoveryPhase | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (recoveryResumedRef.current) return;
    if (config == null || expectedChainId === 0 || preflight.address == null) return;
    if (publicClient == null) return;
    const saleAddr = config.sale_contract_address;
    if (saleAddr == null) return;
    if (purchase.state.kind !== "idle" || indexing.state.kind !== "idle") return;
    const loaded = loadValidRecovery(
      window.localStorage,
      { wallet: preflight.address, chainId: expectedChainId, saleAddr },
      Date.now(),
    );
    if (!loaded.ok) return;
    const c = loaded.ctx;
    let cancelled = false;
    void (async () => {
      // §6 — probe the ALREADY-submitted tx receipt (READ-ONLY; never a new
      // write, never a wallet reopen) so a reverted tx terminates correctly
      // instead of hanging in indexer `delayed` forever.
      let probe: ReceiptProbe;
      try {
        const rc = await publicClient.getTransactionReceipt({
          hash: c.txHash as `0x${string}`,
        });
        probe = { kind: rc.status === "success" ? "success" : "reverted" };
      } catch (e) {
        // viem throws *ReceiptNotFound* while the tx is still pending/unmined;
        // anything else is treated as an RPC outage (degraded, retryable).
        const name = (e as { name?: string } | null)?.name ?? "";
        probe = name.includes("NotFound") ? { kind: "pending" } : { kind: "unavailable" };
      }
      if (cancelled) return;
      // canonicalIndexed is false at resume time — the indexer poll (below)
      // is what establishes canonical ownership; never assumed here.
      const outcome = resolveRecoveryOutcome(probe, false);
      setRecoveryPhase(outcome.phase);
      if (outcome.cleanup) clearRecovery(window.localStorage);
      // 'degraded' (RPC down) stays retryable — do NOT latch so a later render
      // re-probes. Every other outcome is terminal-for-resume and latches.
      if (outcome.trackIndexer || outcome.cleanup || !outcome.retry) {
        recoveryResumedRef.current = true;
      }
      if (outcome.trackIndexer) {
        // Reconstructed snapshot: matchPurchase is IDENTITY-only (txHash +
        // geometry + buyer + sale); price/deadline placeholders are inert.
        // Geometry is context/verification, NEVER authority.
        indexing.controller.start(c.txHash as `0x${string}`, {
          chainId: c.chainId,
          owner: c.wallet as `0x${string}`,
          saleAddress: c.saleAddr as `0x${string}`,
          paymentToken: "0x0000000000000000000000000000000000000000",
          selection: c.geometry,
          maxTotalPrice: 0n,
          quoteAsOfBlock: 0,
          deadline: 0n,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    config,
    expectedChainId,
    preflight.address,
    publicClient,
    purchase.state.kind,
    indexing.state.kind,
    indexing.controller,
  ]);

  // §R — Buy another parcel: explicit user reset of BOTH controllers +
  // clear committed selection via the existing parent callback. No
  // wallet disconnect, no allowance change, no auto-approve, no
  // auto-purchase, no auto-select.
  const onBuyAnotherClick = useCallback(() => {
    indexing.reset();
    purchase.reset();
    onClearSelection?.();
  }, [indexing, purchase, onClearSelection]);

  const onIndexingCheckAgainClick = useCallback(() => {
    indexing.checkAgain();
  }, [indexing]);

  return (
    <div
      data-testid="purchase-panel"
      style={{
        minHeight: "100%",
        color: "#f5f5f7",
        fontFamily: "'JetBrains Mono', ui-monospace, Consolas, monospace",
        padding: "16px",
      }}
    >
      {recoveryPhase != null && recoveryPhase !== "owned" && (
        <div
          data-testid="purchase-recovery-status"
          style={{
            marginBottom: "0.75rem",
            fontSize: "12px",
            color: recoveryPhase === "failed" ? "#ff6b6b" : "#c9c9d1",
          }}
        >
          {recoveryPhase === "confirming" && t("purchase.recovery.confirming")}
          {recoveryPhase === "syncing" && t("purchase.recovery.syncing")}
          {recoveryPhase === "failed" && t("purchase.recovery.failed")}
          {recoveryPhase === "degraded" && t("purchase.recovery.degraded")}
        </div>
      )}
      {/* WO-CONTEST (Claudio) — RIGHT slot must match the LEFT slot: content
          flows DIRECTLY in the drawer body, no container-inside-container.
          This wrapper is now a pass-through (was a bordered #111116 box). */}
      <div
        style={{
          background: "transparent",
          border: "none",
          borderRadius: 0,
          padding: 0,
        }}
      >
        <p style={{ margin: 0, color: "#8a8a94", fontSize: 12, lineHeight: 1.5 }}>
          {t("purchase.intro")}
        </p>

        {bootstrapError && (
          <div
            data-testid="bootstrap-error"
            style={{ color: "#ff6a6a", marginTop: 12, fontSize: 13 }}
          >
            {t("purchase.bootstrapError", { error: bootstrapError })}
          </div>
        )}

        <section style={sectionStyle} data-testid="config-section">
          <h2 style={h2}>{t("purchase.sections.contracts")}</h2>
          {config == null ? (
            <div style={dim}>{t("purchase.loading")}</div>
          ) : config.generation === "v2" ? (
            <div data-testid="config-v2">
              <Row k="generation" v={config.generation} />
              <Row k="chain_id" v={String(config.chain_id)} />
              <Row k="sale_contract_address" v={config.sale_contract_address} />
              <Row k="payment_token" v={config.payment_token} />
              <Row k="parcel_contract" v={config.parcel_contract} />
            </div>
          ) : (
            <div data-testid="config-v1">
              <Row k="generation" v={config.generation} />
              <Row k="state" v={config.state} />
            </div>
          )}
        </section>

        <section style={sectionStyle} data-testid="status-section">
          <h2 style={h2}>{t("purchase.sections.saleStatus")}</h2>
          {status == null ? (
            <div style={dim}>{t("purchase.loading")}</div>
          ) : (
            <div>
              <Row k="generation" v={status.generation} />
              <Row k="state" v={status.state} />
              {status.generation === "v2" && (
                <Row k="paused" v={String(status.paused)} />
              )}
              <Row
                k="canStartPurchase"
                v={
                  gate.canStartPurchase
                    ? t("purchase.gate.yes")
                    : t("purchase.gate.no", { reason: gate.reason })
                }
                testId="can-start-purchase"
              />
            </div>
          )}
        </section>

        <section style={sectionStyle} data-testid="wallet-section">
          <h2 style={h2}>{t("purchase.sections.wallet")}</h2>
          <Row k="status" v={preflight.status} testId="wallet-status" />
          <Row
            k="address"
            v={preflight.address ?? "—"}
            testId="wallet-address"
          />
          <Row
            k="chainId"
            v={preflight.chainId != null ? String(preflight.chainId) : "—"}
            testId="wallet-chain-id"
          />
          <Row
            k="expectedChainId"
            v={String(preflight.expectedChainId)}
            testId="wallet-expected-chain-id"
          />
          {preflight.status === "disconnected" && (
            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {preflight.connectors.map((c) => (
                <button
                  key={c.id}
                  data-testid={`wallet-connect-${c.id}`}
                  disabled={preflight.isConnecting}
                  onClick={() => preflight.connect({ connector: c })}
                  style={btn}
                >
                  {t("purchase.wallet.connect", { name: c.name })}
                </button>
              ))}
            </div>
          )}
          {(preflight.status === "connected" ||
            preflight.status === "wrong_chain") && (
            <button
              data-testid="wallet-disconnect"
              onClick={() => preflight.disconnect()}
              style={btnGhost}
            >
              {t("purchase.wallet.disconnect")}
            </button>
          )}
          {preflight.wrongChain && (
            <div data-testid="wrong-chain-warning" style={warn}>
              {t("purchase.wallet.wrongChain", { chainId: preflight.chainId ?? "", expectedChainId: preflight.expectedChainId })}
              <div style={{ marginTop: 6 }}>
                <button
                  data-testid="switch-network-btn"
                  disabled={chainSwitch.outcome.kind === "switching"}
                  onClick={() =>
                    chainSwitch.switchToExpected(preflight.expectedChainId)
                  }
                  style={btn}
                >
                  {chainSwitch.outcome.kind === "switching"
                    ? t("purchase.wallet.switching")
                    : t("purchase.wallet.switchTo", { chainId: preflight.expectedChainId })}
                </button>
                {chainSwitch.outcome.kind === "rejected" && (
                  <span
                    data-testid="switch-rejected"
                    style={{ marginLeft: 8, fontSize: 12 }}
                  >
                    {t("purchase.wallet.switchRejected")}
                  </span>
                )}
                {chainSwitch.outcome.kind === "error" && (
                  <span
                    data-testid="switch-error"
                    style={{ marginLeft: 8, fontSize: 12 }}
                  >
                    {t("purchase.wallet.switchError", { message: chainSwitch.outcome.message })}
                  </span>
                )}
              </div>
            </div>
          )}
        </section>

        <section style={sectionStyle} data-testid="financial-section">
          <h2 style={h2}>{t("purchase.sections.financial")}</h2>
          {!readGate.allowed ? (
            <div data-testid="financial-blocked" style={dim}>
              {t("purchase.financial.blocked", { reason: readGate.reason })}
            </div>
          ) : (
            <>
              <Row
                k="usdc_balance"
                v={
                  balanceQ.isLoading
                    ? t("purchase.loading")
                    : balanceQ.data != null
                      ? formatMicrousdc(balanceQ.data)
                      : balanceQ.error
                        ? t("purchase.financial.readError")
                        : "—"
                }
                testId="usdc-balance"
              />
              <Row
                k="usdc_allowance"
                v={
                  allowanceQ.isLoading
                    ? t("purchase.loading")
                    : allowanceQ.data != null
                      ? formatMicrousdc(allowanceQ.data)
                      : allowanceQ.error
                        ? t("purchase.financial.readError")
                        : "—"
                }
                testId="usdc-allowance"
              />
              <Row
                k="required_total"
                v={
                  requiredTotal != null
                    ? formatMicrousdc(requiredTotal)
                    : t("purchase.financial.noSellableQuote")
                }
              />
              <Row
                k="readiness"
                v={
                  readiness == null
                    ? "—"
                    : readiness.kind === "insufficient_balance"
                      ? t("purchase.financial.insufficient", { amount: formatMicrousdc(readiness.short_by) })
                      : readiness.kind === "approval_required"
                        ? t("purchase.financial.approval", { amount: formatMicrousdc(readiness.required_approval) })
                        : t("purchase.financial.ready")
                }
                testId="financial-readiness"
              />
              {(balanceQ.error || allowanceQ.error) && (
                <div data-testid="financial-error" style={warn}>
                  {t("purchase.financial.rpcError")}
                </div>
              )}
              <button
                data-testid="financial-refetch"
                onClick={() => { balanceQ.refetch(); allowanceQ.refetch(); }}
                style={btnGhost}
              >
                {t("purchase.financial.refetch")}
              </button>
            </>
          )}
        </section>

        <section style={sectionStyle} data-testid="approve-section">
          <h2 style={h2}>{t("purchase.sections.approval")}</h2>
          {!approveEligibility.eligible ? (
            <div data-testid="approve-blocked" style={dim}>
              {t("purchase.approve.blocked", { reason: approveEligibility.reason })}
            </div>
          ) : (
            <>
              <Row
                k="amount_to_approve"
                v={formatMicrousdc(approveEligibility.snapshot.amount)}
                testId="approve-amount"
              />
              <Row
                k="spender"
                v={approveEligibility.snapshot.spender}
                testId="approve-spender"
              />
              <Row
                k="token"
                v={approveEligibility.snapshot.token}
                testId="approve-token"
              />
              <Row
                k="chain_id"
                v={String(approveEligibility.snapshot.chainId)}
                testId="approve-chain-id"
              />
              <button
                data-testid="approve-btn"
                onClick={onApproveClick}
                disabled={approve.isPending || occupancyBlocked}
                style={btn}
              >
                {approve.isPending
                  ? approve.state.kind === "awaiting_wallet"
                    ? t("purchase.approve.waitingWallet")
                    : approve.state.kind === "submitted"
                      ? t("purchase.approve.submitting")
                      : t("purchase.approve.confirming")
                  : t("purchase.approve.cta")}
              </button>
            </>
          )}
          <Row
            k="approve_state"
            v={approve.state.kind}
            testId="approve-state"
          />
          {"txHash" in approve.state && approve.state.txHash && (
            <Row
              k="tx_hash"
              v={approve.state.txHash}
              testId="approve-tx-hash"
            />
          )}
          {approve.state.kind === "rejected" && (
            <div data-testid="approve-rejected" style={warn}>
              {t("purchase.approve.rejected")}
            </div>
          )}
          {approve.state.kind === "reverted" && (
            <div data-testid="approve-reverted" style={warn}>
              {t("purchase.approve.reverted")}
            </div>
          )}
          {approve.state.kind === "error" && (
            <div data-testid="approve-error" style={warn}>
              {t("purchase.approve.error", { error: approve.state.error })}
            </div>
          )}
          {approve.state.kind === "confirmed" && (
            <div data-testid="approve-confirmed" style={{ color: "#8affaa", fontSize: 12, marginTop: 6 }}>
              {t("purchase.approve.confirmed")}
            </div>
          )}
        </section>

        <section style={sectionStyle} data-testid="purchase-section">
          <h2 style={h2}>{t("purchase.sections.purchase")}</h2>
          {!purchaseEligibility.eligible ? (
            <div data-testid="purchase-blocked" style={dim}>
              {t("purchase.tx.blocked", { reason: purchaseEligibility.reason })}
            </div>
          ) : (
            <button
              data-testid="purchase-btn"
              onClick={() => { void onPurchaseClick(); }}
              disabled={
                purchase.isPending ||
                purchase.state.kind === "confirmed_onchain" ||
                (purchase.state.kind === "error" && purchase.state.txHash != null) ||
                occupancyBlocked
              }
              style={btn}
            >
              {purchase.state.kind === "refreshing_quote"
                ? t("purchase.tx.refreshingQuote")
                : purchase.state.kind === "awaiting_wallet"
                  ? t("purchase.tx.awaitingWallet")
                  : purchase.state.kind === "submitted"
                    ? t("purchase.tx.submitted")
                    : purchase.state.kind === "confirming"
                      ? t("purchase.tx.confirming")
                      : t("purchase.tx.cta")}
            </button>
          )}
          <Row
            k="purchase_state"
            v={purchase.state.kind}
            testId="purchase-state"
          />
          {"snapshot" in purchase.state && purchase.state.snapshot && (
            <>
              <Row
                k="sale_address"
                v={purchase.state.snapshot.saleAddress}
                testId="purchase-sale-address"
              />
              <Row
                k="max_total_price"
                v={formatMicrousdc(purchase.state.snapshot.maxTotalPrice)}
                testId="purchase-max-total-price"
              />
              <Row
                k="deadline"
                v={purchase.state.snapshot.deadline.toString()}
                testId="purchase-deadline"
              />
            </>
          )}
          {"txHash" in purchase.state && purchase.state.txHash && (
            <Row
              k="tx_hash"
              v={purchase.state.txHash}
              testId="purchase-tx-hash"
            />
          )}
          {purchase.state.kind === "rejected" && (
            <div data-testid="purchase-rejected" style={warn}>
              {t("purchase.tx.rejected")}
            </div>
          )}
          {purchase.state.kind === "reverted" && (
            <div data-testid="purchase-reverted" style={warn}>
              {purchase.state.revertReason ? t("purchase.tx.revertedReason", { reason: purchase.state.revertReason }) : t("purchase.tx.reverted") + "."}
            </div>
          )}
          {purchase.state.kind === "error" && (
            <div data-testid="purchase-error" style={warn}>
              {t("purchase.tx.error", { error: purchase.state.error })}
            </div>
          )}
          {purchase.state.kind === "confirmed_onchain" && (
            <div data-testid="purchase-confirmed" style={{ color: "#8affaa", fontSize: 12, marginTop: 6 }}>
              {t("purchase.tx.confirmed")}
            </div>
          )}
        </section>

        {/* SEP 5.e — Backend indexer confirmation section */}
        {indexing.state.kind !== "idle" && (
          <section style={sectionStyle} data-testid="indexing-section">
            <h2 style={h2}>{t("purchase.sections.indexing")}</h2>
            <Row
              k="indexing_state"
              v={indexing.state.kind}
              testId="indexing-state"
            />
            {indexing.state.kind === "indexing_pending" && (
              <>
                <Row
                  k="attempt"
                  v={`${indexing.state.attempt}/${20}`}
                  testId="indexing-attempt"
                />
                <div data-testid="indexing-pending" style={dim}>
                  {t("purchase.indexing.pending")}
                </div>
              </>
            )}
            {indexing.state.kind === "indexing_delayed" && (
              <>
                <div data-testid="indexing-delayed" style={warn}>
                  {t("purchase.indexing.delayed")}
                </div>
                <button
                  data-testid="indexing-check-again-btn"
                  onClick={onIndexingCheckAgainClick}
                  style={btn}
                >
                  {t("purchase.indexing.checkAgain")}
                </button>
              </>
            )}
            {indexing.state.kind === "indexed_successfully" && (
              <>
                <div
                  data-testid="indexing-success"
                  style={{ color: "#8affaa", fontSize: 12, marginTop: 6 }}
                >
                  {t("purchase.indexing.success")}
                </div>
                <Row
                  k="tx_hash"
                  v={indexing.state.txHash}
                  testId="indexing-tx-hash"
                />
                <Row
                  k="parcel_id"
                  v={String(indexing.state.record.parcel_id)}
                  testId="indexing-parcel-id"
                />
                <button
                  data-testid="buy-another-btn"
                  onClick={onBuyAnotherClick}
                  style={btn}
                >
                  {t("purchase.indexing.buyAnother")}
                </button>
              </>
            )}
            {indexing.state.kind === "indexing_anomaly" && (
              <>
                <div data-testid="indexing-anomaly" style={warn}>
                  {t("purchase.indexing.anomaly", { reason: indexing.state.reason })}
                </div>
                <Row
                  k="tx_hash"
                  v={indexing.state.txHash}
                  testId="indexing-anomaly-tx-hash"
                />
                <Row
                  k="anomaly_reason"
                  v={indexing.state.reason}
                  testId="indexing-anomaly-reason"
                />
              </>
            )}
          </section>
        )}

        <section style={sectionStyle} data-testid="quote-section">
          <h2 style={h2}>{t("purchase.sections.quote")}</h2>
          {selection == null ? (
            <div data-testid="selection-empty" style={dim}>
              {t("purchase.quote.empty")}
            </div>
          ) : (
            <>
              <div data-testid="selection-echo" style={{ fontSize: 12 }}>
                selection: x={selection.x} y={selection.y} w={selection.w} h={selection.h}
              </div>
              {onClearSelection && (
                <button
                  data-testid="clear-selection-btn"
                  onClick={onClearSelection}
                  style={btnGhost}
                >
                  {t("purchase.quote.clearSelection")}
                </button>
              )}
              {occupancyBlocked && (
                <div data-testid="selection-occupied-warning" style={warn}>
                  {occupancyGuard.ownedBySelf && !occupancyGuard.ownedByOther
                    ? t("purchase.quote.occupiedSelf")
                    : occupancyGuard.ownedByOther && !occupancyGuard.ownedBySelf
                      ? t("purchase.quote.occupiedOther")
                      : t("purchase.quote.occupiedGeneric")}
                </div>
              )}
            </>
          )}
          {quoteState.kind === "loading" && (
            <div data-testid="quote-loading" style={dim}>
              {t("purchase.quote.loading")}
            </div>
          )}
          {quoteState.kind === "error" && (
            <div data-testid="quote-error" style={warn}>
              <strong>{quoteState.errorCode}</strong> —{" "}
              {quoteErrorText(
                t,
                quoteState,
                selection ? selection.w * selection.h : undefined,
              )}
            </div>
          )}
          {quoteState.kind === "success" && quoteState.quote && (
            <QuoteCard quote={quoteState.quote} />
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * WO-CONTEST (Claudio) — human-readable quote error text. For the shared
 * GEOMETRY_INVALID code we translate the backend `reason` (which mirrors the
 * frozen on-chain gates) into a clear, actionable message instead of the raw
 * "Selection geometry is invalid." — most importantly for an over-sized
 * selection (max 250×250 cells / 10,000 slots per parcel on-chain). Other
 * codes keep their existing category labels.
 */
function quoteErrorText(
  t: (path: string, vars?: Record<string, string | number>) => string,
  state: QuoteState,
  slots?: number,
): string {
  const reason = state.errorReason;
  if (state.errorCode === "GEOMETRY_INVALID") {
    if (reason === "too_wide") {
      return t("purchase.errors.tooWide");
    }
    if (reason === "too_high") {
      return t("purchase.errors.tooHigh");
    }
    if (reason === "area_exceeds_max") {
      const yours = typeof slots === "number" ? t("purchase.errors.areaExceedsYours", { slots: slots.toLocaleString() }) : "";
      return t("purchase.errors.areaExceeds", { yours });
    }
    if (reason === "out_of_bounds") {
      return t("purchase.errors.outOfBounds");
    }
    if (reason === "zero_or_negative_dim") {
      return t("purchase.errors.zeroOrNegative");
    }
    if (reason === "vip_parcel_exceeds_cap") {
      return t("purchase.errors.vipCap");
    }
    if (reason === "parcel_exceeds_zone_cap" || reason === "zone_capacity_exceeded") {
      return t("purchase.errors.zoneCap");
    }
  }
  return state.errorCategory
    ? labelForSaleErrorCategory(state.errorCategory as never)
    : (state.errorMessage ?? t("purchase.errors.unexpected"));
}

function QuoteCard({ quote }: { quote: SaleQuote }) {
  if (quote.generation === "v1") {
    return (
      <div data-testid="quote-v1" style={{ marginTop: 8 }}>
        <Row k="generation" v={quote.generation} />
        <Row k="slots" v={String(quote.slots)} />
        <Row
          k="unit_price"
          v={formatMicrousdc(quote.unit_price_microusdc)}
        />
        <Row
          k="total_price"
          v={formatMicrousdc(quote.total_price_microusdc)}
        />
        <Row k="quote_as_of_block" v={String(quote.quote_as_of_block)} />
      </div>
    );
  }
  return (
    <div data-testid="quote-v2" style={{ marginTop: 8 }}>
      <Row k="generation" v={quote.generation} />
      <Row k="quote_result" v={quote.quote_result} testId="quote-result" />
      <Row k="zone" v={quote.zone} testId="quote-zone" />
      <Row k="sellable" v={String(quote.sellable)} testId="quote-sellable" />
      <Row k="slots" v={String(quote.slots)} testId="quote-slots" />
      <Row
        k="unit_price"
        v={
          quote.unit_price_microusdc == null
            ? "—"
            : formatMicrousdc(quote.unit_price_microusdc)
        }
        testId="quote-unit-price"
      />
      <Row
        k="total_price"
        v={
          quote.total_price_microusdc == null
            ? "—"
            : formatMicrousdc(quote.total_price_microusdc)
        }
        testId="quote-total-price"
      />
      <Row
        k="scarcity_tier"
        v={quote.scarcity_tier == null ? "—" : String(quote.scarcity_tier)}
        testId="quote-scarcity-tier"
      />
      <Row
        k="sold_before"
        v={quote.sold_before == null ? "—" : quote.sold_before.toString()}
      />
      <Row
        k="sellable_capacity"
        v={
          quote.sellable_capacity == null
            ? "—"
            : quote.sellable_capacity.toString()
        }
      />
      <Row
        k="next_threshold_slots_left"
        v={
          quote.next_threshold_slots_left == null
            ? "—"
            : String(quote.next_threshold_slots_left)
        }
      />
      <Row
        k="next_tier_unit_price"
        v={
          quote.next_tier_unit_price_microusdc == null
            ? "—"
            : formatMicrousdc(quote.next_tier_unit_price_microusdc)
        }
      />
      <Row k="quote_as_of_block" v={String(quote.quote_as_of_block)} />
    </div>
  );
}

function Row({
  k,
  v,
  testId,
}: {
  k: string;
  v: string;
  testId?: string;
}) {
  // WO-CONTEST (Claudio) — label ON TOP, value BELOW at full width. The old
  // side-by-side layout squeezed long values (e.g. contract addresses /
  // payment_token) into a narrow right column inside the nested drawer,
  // truncating/wrapping them awkwardly. Stacking uses the full slot width.
  return (
    <div
      style={{ fontSize: 13, padding: "5px 0" }}
      data-testid={testId}
    >
      <div style={{ color: "#8a8a94", fontSize: 11, letterSpacing: "0.04em" }}>{k}</div>
      <div style={{ wordBreak: "break-all", marginTop: 2, color: "#f5f5f7" }}>{v}</div>
    </div>
  );
}

const sectionStyle: React.CSSProperties = {
  marginTop: "1.25rem",
  paddingTop: "1rem",
  borderTop: "1px solid #22222c",
};
const h2: React.CSSProperties = { margin: 0, fontSize: 13, letterSpacing: "0.08em", color: "#8a8a94", textTransform: "uppercase" };
const dim: React.CSSProperties = { color: "#8a8a94", fontSize: 12, marginTop: 6 };
const warn: React.CSSProperties = { color: "#ffb86a", fontSize: 12, marginTop: 6 };
const btn: React.CSSProperties = {
  padding: "8px 12px",
  background: "#f5f5f7",
  color: "#0b0b0f",
  border: "none",
  borderRadius: 6,
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};
const btnGhost: React.CSSProperties = {
  ...btn,
  background: "transparent",
  color: "#f5f5f7",
  border: "1px solid #22222c",
  fontWeight: 500,
  marginTop: 8,
};
